import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ChevronLeft, ChevronRight, CalendarDays, LayoutGrid,
  Plus, Trash2, Save, Send, Users, Truck, X, BookTemplate,
  ClipboardList, CheckCircle, AlertCircle, Clock,
  ChevronDown, Copy, BookOpen, ArrowRightCircle, FileText
} from "lucide-react";

const DAYS_ES = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const SHIFTS = ["MAÑANA", "TARDE", "NOCHE"];
const SERVICE_TYPES = [
  "BARRIDO MANUAL","BARRIDO MANUAL MOTORIZADO","BARRIDO MECÁNICO",
  "BALDEO MANUAL","BALDEO MIXTO","BALDEO MECÁNICO DE CALZADAS",
  "RECOGIDA Y TRANSPORTE FRACCIÓN RESTO CARGA LATERAL",
  "RECOGIDA Y TRANSPORTE DE BASURA DISPERSA","RECOGIDA FRACCIÓN ORGÁNICA",
  "RECOGIDA SELECTIVA","RECOGIDA Y LAVADO CONTENEDORES",
  "INSPECCIÓN","INTERVENCIÓN INMEDIATA","LAVADERO","LIMPIEZA PLAYAS"
];
const CATEGORIES = ["PEÓN","CONDUCTOR","P. CONDUCTOR","INSPECTOR","OFICIAL","ENCARGADO"];

const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

const toISO = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const shiftColor = (s) => s === "MAÑANA" ? "bg-amber-50 text-amber-700 border-amber-200" : s === "TARDE" ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-slate-100 text-slate-600 border-slate-200";
const newEntry = () => ({ shift: "MAÑANA", service_type: "BARRIDO MANUAL", vehicle_plate: "", personnel_name: "", category: "PEÓN", sector_route: "" });

export default function Planning() {
  const navigate = useNavigate();
  const { authAxios } = useAuth();

  const [view, setView] = useState("week"); // week | month
  const [currentDate, setCurrentDate] = useState(new Date());
  const [contracts, setContracts] = useState([]);
  const [contractId, setContractId] = useState("");
  const [plans, setPlans] = useState({}); // date → plan
  const [workReports, setWorkReports] = useState({}); // date → workReport
  const [personnel, setPersonnel] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null); // { date, dayName }
  const [dayEntries, setDayEntries] = useState([]);
  const [dayPlanId, setDayPlanId] = useState(null);
  const [activeTab, setActiveTab] = useState("plan"); // plan | compare
  const [saving, setSaving] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Compute date range
  const weekStart = getMonday(currentDate);
  const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

  const dateFrom = view === "week" ? toISO(weekStart) : toISO(monthStart);
  const dateTo = view === "week" ? toISO(addDays(weekStart, 6)) : toISO(monthEnd);

  // Load contracts once
  useEffect(() => {
    authAxios.get("/contracts").then(r => {
      setContracts(r.data);
      if (r.data.length > 0) setContractId(r.data[0].id);
    });
  }, []);

  // Load personnel + vehicles when contract changes
  useEffect(() => {
    if (!contractId) return;
    Promise.all([
      authAxios.get(`/personnel?contract_id=${contractId}&active=true`),
      authAxios.get(`/vehicles?contract_id=${contractId}&status=active`),
      authAxios.get(`/plans/templates?contract_id=${contractId}`)
    ]).then(([p, v, t]) => {
      setPersonnel(p.data);
      setVehicles(v.data);
      setTemplates(t.data);
    });
  }, [contractId]);

  // Load plans + work reports for visible range
  const loadData = useCallback(async () => {
    if (!contractId) return;
    const [plansRes, reportsRes] = await Promise.all([
      authAxios.get(`/plans?contract_id=${contractId}&date_from=${dateFrom}&date_to=${dateTo}`),
      authAxios.get(`/work-reports?contract_id=${contractId}&limit=100`)
    ]);
    const planMap = {};
    plansRes.data.forEach(p => { planMap[p.date] = p; });
    setPlans(planMap);

    const reportMap = {};
    reportsRes.data.forEach(r => { if (r.date >= dateFrom && r.date <= dateTo) reportMap[r.date] = r; });
    setWorkReports(reportMap);
  }, [contractId, dateFrom, dateTo]);

  useEffect(() => { loadData(); }, [loadData]);

  // Navigate
  const prev = () => {
    if (view === "week") setCurrentDate(d => addDays(d, -7));
    else setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  };
  const next = () => {
    if (view === "week") setCurrentDate(d => addDays(d, 7));
    else setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  };
  const goToday = () => setCurrentDate(new Date());

  // Open day detail
  const openDay = (dateStr, dayName) => {
    const plan = plans[dateStr];
    setSelectedDay({ date: dateStr, dayName });
    setDayEntries(plan ? [...plan.entries.map(e => ({ ...e }))] : []);
    setDayPlanId(plan?.id || null);
    setActiveTab("plan");
  };

  const closeDay = () => setSelectedDay(null);

  const addEntry = () => setDayEntries(prev => [...prev, newEntry()]);
  const removeEntry = (i) => setDayEntries(prev => prev.filter((_, idx) => idx !== i));
  const updateEntry = (i, field, value) => setDayEntries(prev => prev.map((e, idx) => idx === i ? { ...e, [field]: value } : e));

  // Save plan for selected day
  const savePlan = async () => {
    if (!selectedDay || !contractId) return;
    setSaving(true);
    try {
      const r = await authAxios.post("/plans", {
        contract_id: contractId,
        date: selectedDay.date,
        day_of_week: selectedDay.dayName,
        entries: dayEntries.map(e => ({
          ...e,
          vehicle_plate: e.vehicle_plate || null,
          personnel_name: e.personnel_name || null,
          sector_route: e.sector_route || null
        }))
      });
      setPlans(prev => ({ ...prev, [selectedDay.date]: r.data }));
      setDayPlanId(r.data.id);
    } finally {
      setSaving(false);
    }
  };

  // Delete plan for selected day
  const deletePlan = async () => {
    if (!dayPlanId || !window.confirm("¿Eliminar la planificación de este día?")) return;
    await authAxios.delete(`/plans/${dayPlanId}`);
    setPlans(prev => { const n = { ...prev }; delete n[selectedDay.date]; return n; });
    setDayEntries([]);
    setDayPlanId(null);
  };

  // Generate work report from plan
  const generateReport = () => {
    if (!selectedDay || dayEntries.length === 0) return;
    navigate("/work-reports/new", {
      state: {
        contractId,
        planEntries: dayEntries,
        date: selectedDay.date,
        dayOfWeek: selectedDay.dayName
      }
    });
  };

  // Save current week as template
  const saveAsTemplate = async () => {
    if (!templateName.trim()) return;
    const r = await authAxios.post("/plans/save-template", {
      contract_id: contractId,
      week_start: toISO(weekStart),
      name: templateName
    });
    setTemplates(prev => [r.data, ...prev]);
    setTemplateName("");
    setShowTemplateModal(false);
  };

  // Apply template to current week
  const applyTemplate = async () => {
    if (!selectedTemplate) return;
    const r = await authAxios.post("/plans/apply-template", {
      template_id: selectedTemplate,
      target_week_start: toISO(weekStart),
      contract_id: contractId
    });
    await loadData();
    setShowApplyModal(false);
    setSelectedTemplate(null);
  };

  // Comparison logic
  const compare = () => {
    if (!selectedDay) return null;
    const report = workReports[selectedDay.date];
    if (!report) return null;
    const planned = dayEntries;
    const actual = report.entries || [];

    return planned.map(pe => {
      const match = actual.find(ae =>
        ae.personnel_name === pe.personnel_name && ae.service_type === pe.service_type
      );
      return {
        planned: pe,
        actual: match,
        status: match ? (match.completed ? "ok" : "ko") : "absent"
      };
    }).concat(
      actual
        .filter(ae => !planned.find(pe => pe.personnel_name === ae.personnel_name && pe.service_type === ae.service_type))
        .map(ae => ({ planned: null, actual: ae, status: "extra" }))
    );
  };

  const contract = contracts.find(c => c.id === contractId);
  const color = contract?.color || "#007934";

  // ── Week View ──────────────────────────────────────────────────────────────
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(weekStart, i);
    return { date: toISO(d), dayShort: DAYS_ES[i].slice(0, 3), dayName: DAYS_ES[i], d };
  });

  // ── Month View ─────────────────────────────────────────────────────────────
  const firstWeekday = (monthStart.getDay() + 6) % 7; // Mon=0
  const daysInMonth = monthEnd.getDate();
  const monthCells = [];
  for (let i = 0; i < firstWeekday; i++) monthCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    monthCells.push(new Date(currentDate.getFullYear(), currentDate.getMonth(), d));
  }

  const today = toISO(new Date());

  return (
    <div className="flex h-full overflow-hidden">
      {/* Main calendar area */}
      <div className={`flex flex-col flex-1 min-w-0 overflow-hidden transition-all duration-300 ${selectedDay ? "mr-0" : ""}`}>
        {/* Toolbar */}
        <div className="glass-header px-6 py-3 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3 flex-wrap">
            <CalendarDays size={20} className="text-[#007934]" />
            <h1 className="text-lg font-black text-slate-900">Planificación de Turnos</h1>
            <div className="flex items-center gap-1 mx-2">
              <button onClick={prev} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"><ChevronLeft size={16} /></button>
              <button onClick={goToday} className="px-3 py-1 text-xs font-semibold bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">Hoy</button>
              <button onClick={next} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"><ChevronRight size={16} /></button>
            </div>
            <span className="text-sm font-semibold text-slate-700 min-w-40">
              {view === "week"
                ? `${weekDays[0].d.getDate()} ${MONTHS_ES[weekDays[0].d.getMonth()].slice(0,3)} – ${weekDays[6].d.getDate()} ${MONTHS_ES[weekDays[6].d.getMonth()].slice(0,3)} ${weekDays[0].d.getFullYear()}`
                : `${MONTHS_ES[currentDate.getMonth()]} ${currentDate.getFullYear()}`
              }
            </span>

            <div className="ml-auto flex items-center gap-2">
              {/* Contract selector */}
              <select
                data-testid="planning-contract-select"
                value={contractId}
                onChange={e => setContractId(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/20"
              >
                {contracts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>

              {/* View toggle */}
              <div className="flex bg-slate-100 rounded-lg p-0.5">
                <button
                  data-testid="week-view-btn"
                  onClick={() => setView("week")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${view === "week" ? "bg-white shadow text-slate-800" : "text-slate-500"}`}
                >
                  <LayoutGrid size={13} /> Semana
                </button>
                <button
                  data-testid="month-view-btn"
                  onClick={() => setView("month")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${view === "month" ? "bg-white shadow text-slate-800" : "text-slate-500"}`}
                >
                  <CalendarDays size={13} /> Mes
                </button>
              </div>

              {/* Template actions (week view only) */}
              {view === "week" && (
                <>
                  <button
                    data-testid="save-template-btn"
                    onClick={() => setShowTemplateModal(true)}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg bg-white hover:bg-slate-50 text-slate-600 transition-all"
                  >
                    <Save size={13} /> Guardar plantilla
                  </button>
                  {templates.length > 0 && (
                    <button
                      data-testid="apply-template-btn"
                      onClick={() => setShowApplyModal(true)}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-[#007934] hover:bg-[#005c27] text-white rounded-lg shadow-sm transition-all"
                    >
                      <Copy size={13} /> Aplicar plantilla
                    </button>
                  )}
                  <button
                    data-testid="manage-templates-btn"
                    onClick={() => navigate(`/planning/templates?contract_id=${contractId}`)}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-slate-200 rounded-lg bg-white hover:bg-slate-50 text-slate-600 transition-all"
                    title="Editor avanzado de plantillas: crear, renombrar, duplicar, editar 7 días"
                  >
                    <FileText size={13} /> Gestionar plantillas
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div className="flex-1 overflow-auto p-4">
          {/* ── Week view ── */}
          {view === "week" && (
            <div className="grid grid-cols-7 gap-2 min-h-full">
              {weekDays.map(({ date, dayShort, dayName, d }) => {
                const plan = plans[date];
                const report = workReports[date];
                const isToday = date === today;
                const isPast = date < today;
                const missingReport = isPast && plan && !report;
                const isSelected = selectedDay?.date === date;
                return (
                  <div
                    key={date}
                    data-testid={`day-cell-${date}`}
                    onClick={() => openDay(date, dayName)}
                    className={`flex flex-col rounded-xl border-2 cursor-pointer transition-all hover:shadow-md min-h-40
                      ${isSelected ? "border-[#007934] shadow-md" : missingReport ? "border-amber-300 hover:border-amber-400" : "border-slate-200 hover:border-slate-300"}
                      ${isToday ? "bg-[#E6F4EA]" : missingReport ? "bg-amber-50/30" : "bg-white"}
                    `}
                  >
                    {/* Day header */}
                    <div className={`px-3 pt-3 pb-2 border-b ${isToday ? "border-[#007934]/20" : missingReport ? "border-amber-200" : "border-slate-100"}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-xs font-semibold uppercase tracking-widest ${isToday ? "text-[#007934]" : "text-slate-400"}`}>{dayShort}</p>
                          <p className={`text-xl font-black leading-tight ${isToday ? "text-[#007934]" : "text-slate-800"}`}>{d.getDate()}</p>
                        </div>
                        {missingReport && (
                          <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full border border-amber-200">
                            Sin parte
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Plan summary */}
                    <div className="flex-1 p-2 space-y-1 overflow-hidden">
                      {plan && plan.entries.slice(0, 4).map((e, i) => (
                        <div key={i} className={`text-[10px] px-1.5 py-0.5 rounded border font-medium truncate ${shiftColor(e.shift)}`}>
                          {e.personnel_name || e.service_type}
                        </div>
                      ))}
                      {plan && plan.entries.length > 4 && (
                        <p className="text-[10px] text-slate-400 pl-1">+{plan.entries.length - 4} más</p>
                      )}
                      {!plan && (
                        <div className="flex items-center justify-center h-full pt-3">
                          <Plus size={16} className="text-slate-300" />
                        </div>
                      )}
                    </div>

                    {/* Badges */}
                    <div className="px-2 pb-2 flex flex-wrap gap-1">
                      {plan && (
                        <span className="text-[10px] bg-[#E6F4EA] text-[#007934] font-semibold px-1.5 py-0.5 rounded-full">
                          {plan.total_planned_staff}p {plan.total_planned_vehicles > 0 ? `· ${plan.total_planned_vehicles}v` : ""}
                        </span>
                      )}
                      {report && (
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${report.completion_rate >= 95 ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                          {report.completion_rate}%
                        </span>
                      )}
                      {report?.reliability === "review" && (
                        <span className="text-[9px] font-bold bg-red-50 text-red-500 px-1.5 py-0.5 rounded-full border border-red-200">
                          Revisar
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Month view ── */}
          {view === "month" && (
            <div>
              {/* Header days */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {DAYS_ES.map(d => (
                  <div key={d} className="text-center text-xs font-semibold text-slate-400 uppercase tracking-widest py-2">{d.slice(0,3)}</div>
                ))}
              </div>
              {/* Cells */}
              <div className="grid grid-cols-7 gap-1">
                {monthCells.map((d, i) => {
                  if (!d) return <div key={`empty-${i}`} />;
                  const dateStr = toISO(d);
                  const dayIdx = (d.getDay() + 6) % 7;
                  const plan = plans[dateStr];
                  const report = workReports[dateStr];
                  const isToday = dateStr === today;
                  const isPast = dateStr < today;
                  const missingReport = isPast && plan && !report;
                  const isSelected = selectedDay?.date === dateStr;
                  return (
                    <div
                      key={dateStr}
                      data-testid={`month-cell-${dateStr}`}
                      onClick={() => openDay(dateStr, DAYS_ES[dayIdx])}
                      className={`rounded-xl border-2 cursor-pointer transition-all hover:shadow-sm min-h-20 p-2
                        ${isSelected ? "border-[#007934]" : missingReport ? "border-amber-300" : "border-slate-200 hover:border-slate-300"}
                        ${isToday ? "bg-[#E6F4EA]" : missingReport ? "bg-amber-50/40" : "bg-white"}
                      `}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <p className={`text-sm font-black ${isToday ? "text-[#007934]" : "text-slate-700"}`}>{d.getDate()}</p>
                        {missingReport && <span className="text-[8px] font-bold text-amber-600">●</span>}
                      </div>
                      {plan && (
                        <div className="flex items-center gap-1 mb-0.5">
                          <Users size={9} className="text-[#007934]" />
                          <span className="text-[10px] text-[#007934] font-semibold">{plan.total_planned_staff}</span>
                        </div>
                      )}
                      {report && (
                        <div className="space-y-0.5">
                          <div className={`text-[10px] font-semibold px-1 py-0.5 rounded text-center ${report.completion_rate >= 95 ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                            {report.completion_rate}%
                          </div>
                          {report.reliability === "review" && (
                            <div className="text-[8px] font-bold text-center text-red-500">Revisar</div>
                          )}
                        </div>
                      )}
                      {!plan && !report && (
                        <div className="w-3 h-0.5 bg-slate-200 rounded mx-auto mt-2" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Day Detail Drawer ── */}
      {selectedDay && (
        <div className="w-[520px] flex-shrink-0 border-l border-slate-200 bg-white flex flex-col overflow-hidden animate-scale-in">
          {/* Drawer header */}
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3 flex-shrink-0">
            <div className="flex-1">
              <p className="text-xs text-slate-400 uppercase tracking-widest font-semibold">{selectedDay.dayName}</p>
              <h2 className="text-lg font-black text-slate-900">{selectedDay.date}</h2>
            </div>
            <div className="flex items-center gap-2">
              {dayPlanId && (
                <button
                  data-testid="delete-plan-btn"
                  onClick={deletePlan}
                  className="p-2 text-slate-400 hover:text-red-400 rounded-lg hover:bg-red-50 transition-all"
                  title="Eliminar planificación"
                >
                  <Trash2 size={15} />
                </button>
              )}
              <button onClick={closeDay} className="p-2 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 transition-all">
                <X size={15} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-100 flex-shrink-0">
            {[
              { id: "plan", label: "Planificación", icon: CalendarDays },
              { id: "compare", label: "vs. Ejecución", icon: CheckCircle, disabled: !workReports[selectedDay.date] }
            ].map(tab => (
              <button
                key={tab.id}
                data-testid={`drawer-tab-${tab.id}`}
                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                disabled={tab.disabled}
                className={`flex items-center gap-1.5 px-4 py-3 text-xs font-semibold border-b-2 transition-all -mb-px flex-1 justify-center
                  ${activeTab === tab.id ? "border-[#007934] text-[#007934]" : "border-transparent text-slate-400 hover:text-slate-600"}
                  ${tab.disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}
                `}
              >
                <tab.icon size={13} />
                {tab.label}
                {tab.id === "compare" && !tab.disabled && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#007934]" />
                )}
              </button>
            ))}
          </div>

          {/* ── Plan Tab ── */}
          {activeTab === "plan" && (
            <div className="flex-1 overflow-auto">
              <div className="p-4 space-y-2">
                {dayEntries.map((entry, i) => (
                  <div
                    key={i}
                    data-testid={`plan-entry-${i}`}
                    className="bg-slate-50 rounded-xl p-3 space-y-2 border border-slate-200"
                  >
                    <div className="flex items-center gap-2">
                      <select
                        value={entry.shift}
                        onChange={e => updateEntry(i, "shift", e.target.value)}
                        className={`text-xs border rounded-lg px-2 py-1 font-semibold ${shiftColor(entry.shift)}`}
                      >
                        {SHIFTS.map(s => <option key={s}>{s}</option>)}
                      </select>
                      <button
                        data-testid={`remove-plan-entry-${i}`}
                        onClick={() => removeEntry(i)}
                        className="ml-auto text-slate-300 hover:text-red-400 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-slate-400 font-semibold uppercase">Servicio</label>
                        <select
                          value={entry.service_type}
                          onChange={e => updateEntry(i, "service_type", e.target.value)}
                          className="w-full mt-0.5 text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-[#007934]"
                        >
                          {SERVICE_TYPES.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-400 font-semibold uppercase">Matrícula</label>
                        <input
                          list={`veh-${i}`}
                          value={entry.vehicle_plate || ""}
                          onChange={e => updateEntry(i, "vehicle_plate", e.target.value)}
                          placeholder="Matrícula..."
                          className="w-full mt-0.5 text-xs border border-slate-200 rounded-lg px-2 py-1.5 font-mono focus:outline-none focus:ring-1 focus:ring-[#007934]"
                        />
                        <datalist id={`veh-${i}`}>
                          {vehicles.map(v => <option key={v.id} value={v.plate}>{v.plate} – {v.brand}</option>)}
                        </datalist>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-400 font-semibold uppercase">Personal</label>
                        <input
                          list={`per-${i}`}
                          value={entry.personnel_name || ""}
                          onChange={e => updateEntry(i, "personnel_name", e.target.value)}
                          placeholder="APELLIDO, Nombre"
                          className="w-full mt-0.5 text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#007934]"
                        />
                        <datalist id={`per-${i}`}>
                          {personnel.map(p => <option key={p.id} value={p.full_name}>{p.full_name} – {p.category}</option>)}
                        </datalist>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-400 font-semibold uppercase">Sector / Ruta</label>
                        <input
                          value={entry.sector_route || ""}
                          onChange={e => updateEntry(i, "sector_route", e.target.value)}
                          placeholder="SECTOR 1..."
                          className="w-full mt-0.5 text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#007934]"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  data-testid="add-plan-entry-btn"
                  onClick={addEntry}
                  className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-slate-200 rounded-xl text-sm text-slate-400 hover:border-[#007934] hover:text-[#007934] transition-all"
                >
                  <Plus size={15} /> Añadir entrada
                </button>
              </div>
            </div>
          )}

          {/* ── Compare Tab ── */}
          {activeTab === "compare" && (() => {
            const cmp = compare();
            const report = workReports[selectedDay.date];
            if (!cmp || !report) return null;
            const ok = cmp.filter(c => c.status === "ok").length;
            const ko = cmp.filter(c => c.status === "ko").length;
            const absent = cmp.filter(c => c.status === "absent").length;
            const extra = cmp.filter(c => c.status === "extra").length;
            return (
              <div className="flex-1 overflow-auto p-4">
                {/* Summary */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {[
                    { label: "Completados", value: ok, color: "text-emerald-600", bg: "bg-emerald-50" },
                    { label: "No completados", value: ko, color: "text-red-500", bg: "bg-red-50" },
                    { label: "Ausentes", value: absent, color: "text-amber-600", bg: "bg-amber-50" },
                    { label: "Extra", value: extra, color: "text-blue-600", bg: "bg-blue-50" },
                  ].map(s => (
                    <div key={s.label} className={`${s.bg} rounded-xl p-3 text-center`}>
                      <p className={`text-xl font-black ${s.color}`}>{s.value}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Comparison rows */}
                <div className="space-y-2">
                  {cmp.map((row, i) => (
                    <div
                      key={i}
                      data-testid={`compare-row-${i}`}
                      className={`rounded-xl p-3 border text-xs
                        ${row.status === "ok" ? "bg-emerald-50 border-emerald-200" :
                          row.status === "ko" ? "bg-red-50 border-red-200" :
                          row.status === "absent" ? "bg-amber-50 border-amber-200" :
                          "bg-blue-50 border-blue-200"}
                      `}
                    >
                      <div className="flex items-start gap-2">
                        <div className="mt-0.5 flex-shrink-0">
                          {row.status === "ok" && <CheckCircle size={14} className="text-emerald-600" />}
                          {row.status === "ko" && <AlertCircle size={14} className="text-red-500" />}
                          {row.status === "absent" && <Clock size={14} className="text-amber-600" />}
                          {row.status === "extra" && <Plus size={14} className="text-blue-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-slate-800 truncate">
                              {row.planned?.personnel_name || row.actual?.personnel_name || "—"}
                            </span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold
                              ${row.status === "ok" ? "bg-emerald-100 text-emerald-700" :
                                row.status === "ko" ? "bg-red-100 text-red-700" :
                                row.status === "absent" ? "bg-amber-100 text-amber-700" :
                                "bg-blue-100 text-blue-700"}
                            `}>
                              {row.status === "ok" ? "OK" : row.status === "ko" ? "KO" : row.status === "absent" ? "AUSENTE" : "EXTRA"}
                            </span>
                          </div>
                          <div className="text-slate-500 mt-0.5 space-y-0.5">
                            <p>Planificado: {row.planned?.service_type || "—"} {row.planned?.sector_route ? `· ${row.planned.sector_route}` : ""}</p>
                            {row.actual && (
                              <p>Ejecutado: {row.actual.service_type} {row.actual.observations ? `· ${row.actual.observations}` : ""}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 p-3 bg-slate-50 rounded-xl border border-slate-200 text-center">
                  <p className="text-xs text-slate-500">Parte del día · Cumplimiento real:</p>
                  <p className={`text-2xl font-black mt-1 ${report.completion_rate >= 95 ? "text-emerald-600" : report.completion_rate >= 80 ? "text-amber-500" : "text-red-500"}`}>
                    {report.completion_rate}%
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Drawer footer - only in plan tab */}
          {activeTab === "plan" && (
          <div className="border-t border-slate-100 p-4 flex gap-2 flex-shrink-0">
            <button
              data-testid="save-plan-btn"
              onClick={savePlan}
              disabled={saving || dayEntries.length === 0}
              className="flex-1 flex items-center justify-center gap-2 bg-[#007934] hover:bg-[#005c27] text-white py-2.5 rounded-xl text-sm font-semibold shadow-md transition-all active:scale-95 disabled:opacity-50"
            >
              <Save size={15} /> {saving ? "Guardando..." : "Guardar"}
            </button>
            {dayEntries.length > 0 && (
              <button
                data-testid="generate-report-btn"
                onClick={generateReport}
                className="flex items-center gap-1.5 px-4 py-2.5 border-2 border-[#007934] text-[#007934] rounded-xl text-sm font-semibold hover:bg-[#E6F4EA] transition-all"
              >
                <ArrowRightCircle size={15} /> Generar Parte
              </button>
            )}
          </div>
          )}
        </div>
      )}

      {/* ── Save Template Modal ── */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-scale-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900">Guardar como plantilla</h3>
              <button onClick={() => setShowTemplateModal(false)}><X size={16} className="text-slate-400" /></button>
            </div>
            <p className="text-xs text-slate-500 mb-4">Guarda la planificación de la semana actual como plantilla reutilizable.</p>
            <input
              data-testid="template-name-input"
              value={templateName}
              onChange={e => setTemplateName(e.target.value)}
              placeholder="Ej: Semana tipo Barakaldo verano"
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#007934]/20"
            />
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowTemplateModal(false)} className="flex-1 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button
                data-testid="confirm-save-template-btn"
                onClick={saveAsTemplate}
                disabled={!templateName.trim()}
                className="flex-1 py-2 bg-[#007934] text-white rounded-xl text-sm font-semibold disabled:opacity-50"
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Apply Template Modal ── */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-scale-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-900">Aplicar plantilla</h3>
              <button onClick={() => setShowApplyModal(false)}><X size={16} className="text-slate-400" /></button>
            </div>
            <p className="text-xs text-slate-500 mb-4">Selecciona una plantilla para aplicarla a la semana del <strong>{toISO(weekStart)}</strong>.</p>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {templates.map(t => (
                <label key={t.id} className="flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all hover:border-[#007934]/40"
                  style={{ borderColor: selectedTemplate === t.id ? "#007934" : undefined }}>
                  <input
                    type="radio"
                    name="template"
                    data-testid={`template-option-${t.id}`}
                    checked={selectedTemplate === t.id}
                    onChange={() => setSelectedTemplate(t.id)}
                    className="accent-[#007934]"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 text-sm truncate">{t.name}</p>
                    <p className="text-xs text-slate-400">Creada el {new Date(t.created_at).toLocaleDateString("es-ES")} · Semana {t.week_start}</p>
                  </div>
                  <div className="text-xs text-slate-400 flex-shrink-0">{Object.keys(t.days || {}).length} {Object.keys(t.days || {}).length === 1 ? "día" : "días"}</div>
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => { setShowApplyModal(false); setSelectedTemplate(null); }} className="flex-1 py-2 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button
                data-testid="confirm-apply-template-btn"
                onClick={applyTemplate}
                disabled={!selectedTemplate}
                className="flex-1 py-2 bg-[#007934] text-white rounded-xl text-sm font-semibold disabled:opacity-50"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
